import { MdOutlineCable, MdOutlineDelete } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import {
  useNodes,
  Node,
  NodeToolbar,
  useStoreApi,
  getRectOfNodes,
  useReactFlow,
  Position,
} from "reactflow";
import { v4 as uuidv4 } from "uuid";

import { ToastType, useShowToast } from "@/components/toast";
import { Button } from "@/design/components/button";
import {
  getPendingRequests,
  triggerAutoSave,
  workflowStatus,
} from "@/features/workflow-studio/redux";
import { useAppSelector } from "@/reduxHooks";
import { ModalTypes, openModal } from "@/slices/modal-slice";
import { PRIVACY_STATUS } from "@/utils/enums";

import {
  NODE_PREVENT_DELETE_STATUSES,
  NODE_STATUS,
} from "../../utils/constants";
import { validateNodeforGrouping } from "../../utils/validations";

const padding = 25;

export default function MultiSelectNodeToolbar() {
  const nodes = useNodes();
  const dispatch = useDispatch();
  const { setNodes, deleteElements } = useReactFlow();
  const toast = useShowToast(undefined, undefined, true);
  const pendingRequests = useSelector(getPendingRequests);

  const store = useStoreApi();
  const selectedNodes = nodes.filter(
    (node) => node.selected && !node.parentNode
  );
  const selectedNodeIds = selectedNodes.map((node) => node.id);
  const isVisible = selectedNodeIds.length > 1;

  const autoSave = () => {
    setTimeout(() => {
      dispatch(triggerAutoSave());
    }, 300);
  };

  const onGroup = () => {
    const groupingError = validateNodeforGrouping(selectedNodes);
    if (groupingError !== "") {
      toast({
        description: groupingError,
        variant: "left-accent",
        status: "warning",
      });
      return;
    }
    const rectOfNodes = getRectOfNodes(selectedNodes);
    const groupId = uuidv4();
    const parentPosition = {
      x: rectOfNodes.x,
      y: rectOfNodes.y,
    };
    const groupNode = {
      id: groupId,
      type: "group-node",
      position: parentPosition,
      style: {
        width: rectOfNodes.width + padding * 2,
        height: rectOfNodes.height + padding * 2,
      },
      data: {
        displayName: "Untitled Flow",
        name: "Untitled Flow",
        displayText: "Untitled Flow",
        description: null,
        clientId: null,
        userId: null,
        nodeType: "CUSTOM_FLOW_NODE",
        isFavorite: false,
        uploadDate: new Date().toLocaleString(),
        lastUsedAt: new Date().toLocaleString(),
        tags: [],
        privacyStatus: PRIVACY_STATUS.PUBLIC,
      },
    };

    const nextNodes: Node[] = nodes.map((node) => {
      if (selectedNodeIds.includes(node.id)) {
        return {
          ...node,
          position: {
            x: node.position.x - parentPosition.x + padding,
            y: node.position.y - parentPosition.y + padding,
          },
          selected: false,
          extent: "parent",
          parentNode: groupId,
        };
      }

      return node;
    });

    store.getState().resetSelectedElements();
    store.setState({ nodesSelectionActive: false });
    setNodes([groupNode, ...nextNodes]);
    autoSave();
  };

  const removeNodes = () => {
    deleteElements({ nodes: selectedNodeIds.map((id) => ({ id })) });
    store.getState().resetSelectedElements();
    store.setState({ nodesSelectionActive: false });
    // autoSave will be called by onNodeDelete in reactflow
  };


  const wfStatus = useAppSelector(workflowStatus);

  const handleDelete = () => {
    // Check if any of the selected nodes are running or locked
    const hasRunningOrLockedNodes = selectedNodes.some((node) => {
      const currentNodeStatus = wfStatus?.workflowNodeStatus.find(
        (status) => status.uiNodeId === node.id
      )?.nodeStatus;
      const isPending = pendingRequests.includes(node.id);
      const preventDelete = NODE_PREVENT_DELETE_STATUSES.includes(
        currentNodeStatus as NODE_STATUS
      );

      return preventDelete || isPending;
    });

    if (hasRunningOrLockedNodes) {
      toast({
        title: "Cannot remove flow during execution",
        description: "One or more nodes in this flow are currently executing. Terminate execution or wait for them to complete.",
        status: ToastType.Warning,
      });
      return;
    }

    dispatch(
      openModal({
        modalType: ModalTypes.DELETE_NODE,
        modalProps: {
          nodeData: selectedNodes.map((node) => node.data),
          onConfirmDelete: removeNodes,
        },
      })
    );
  };

  return (
    <NodeToolbar
      position={Position.Top}
      className="bg-white p-1 flex flex-col items-start border border-gray-400"
      nodeId={selectedNodeIds}
      isVisible={isVisible}
    >
      <Button
        variant="ghost"
        className="!p-1"
        size="sm"
        colorScheme="dark"
        leftIcon={<MdOutlineCable className="rotate-90" />}
        onClick={onGroup}
      >
        Group selected nodes
      </Button>
      <hr className="border-black" />
      <Button
        variant="ghost"
        className="!p-1 w-full !justify-start"
        size="sm"
        colorScheme="red"
        leftIcon={<MdOutlineDelete />}
        onClick={handleDelete}
      >
        Remove nodes
      </Button>
    </NodeToolbar>
  );
}

// const MultiSelectNodeToolbar = () => {
//   return (
//     <div className="absolute -z-10 -top-8 left-1/2 -translate-x-1/2 w-full px-3 h-full">
//       <NodeToolbar>
//         <Button variant="outline" colorScheme="orange">
//           Group selected nodes
//         </Button>
//       </NodeToolbar>
//     </div>
//   );
// };
